import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Mail, Database, Calculator, FileSearch, MessageSquare } from 'lucide-react';

const useCases = [
  {
    icon: <FileText className="w-6 h-6 text-orcsys-500" />,
    title: "Traitement automatique de documents",
    description: "Automatisez l'extraction et le traitement des données de vos factures, bons de commande et formulaires.",
    metrics: [
      "Réduction de 80% du temps de traitement",
      "Précision de 99,9%",
      "Économies de 50K€/an"
    ]
  },
  {
    icon: <Mail className="w-6 h-6 text-orcsys-500" />,
    title: "Gestion des emails et support",
    description: "Triez, catégorisez et répondez automatiquement aux emails avec l'IA pour un support client 24/7.",
    metrics: [
      "Temps de réponse < 5 minutes",
      "90% de résolution automatique",
      "Satisfaction client +40%"
    ]
  },
  {
    icon: <Database className="w-6 h-6 text-orcsys-500" />,
    title: "Intégration de données",
    description: "Synchronisez automatiquement vos données entre différents systèmes et bases de données.",
    metrics: [
      "Zéro erreur de saisie",
      "Gain de 15h/semaine",
      "ROI en 3 mois"
    ]
  },
  {
    icon: <Calculator className="w-6 h-6 text-orcsys-500" />,
    title: "Automatisation comptable",
    description: "Automatisez la saisie comptable, le rapprochement bancaire et la génération de rapports.",
    metrics: [
      "Réduction de 70% des tâches manuelles",
      "Conformité 100%",
      "Économies de 40K€/an"
    ]
  },
  {
    icon: <FileSearch className="w-6 h-6 text-orcsys-500" />,
    title: "Analyse de contrats",
    description: "Analysez automatiquement les contrats pour en extraire les informations clés et les risques.",
    metrics: [
      "Analyse 50x plus rapide",
      "Détection de 99% des risques",
      "Réduction des litiges de 60%"
    ]
  },
  {
    icon: <MessageSquare className="w-6 h-6 text-orcsys-500" />,
    title: "Chatbot IA",
    description: "Déployez un assistant virtuel intelligent pour répondre aux questions fréquentes 24/7.",
    metrics: [
      "Disponibilité 24/7",
      "Taux de satisfaction 92%",
      "ROI en 6 mois"
    ]
  }
];

const UseCasesSection = () => {
  return (
    <section className="py-20 lg:py-24 bg-slate-50">
      <div className="container px-4 mx-auto">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Des solutions <span className="text-orcsys-500">concrètes</span> pour votre entreprise
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Découvrez comment nos solutions d'automatisation s'appliquent à vos besoins 
                spécifiques avec des résultats mesurables.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-orcsys-50 flex items-center justify-center mb-4">
                  {useCase.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {useCase.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {useCase.description}
                </p>
                <ul className="space-y-2">
                  {useCase.metrics.map((metric, i) => (
                    <li key={i} className="flex items-center text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-orcsys-500 mr-2" />
                      <span className="text-slate-700 font-medium">{metric}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection; 